import Storages from '@/core/Storages';
import ITable, { ITableColumn } from '@/core/Values/ITable';
import { computed, h, ref, resolveDirective, withDirectives } from 'vue';
import vueI18n from '@/plugins/VueI18n';
import ITask from '@/core/Models/ITask';
import VUserCard from '@/components/VUserCard.vue';
import CollaboratorRole from '@/core/Values/CollaboratorRole';
import { formatDateOnly } from '@/utils/date-utils';
import Status from '@/core/Values/Status';
import { DateTime } from 'luxon';
import VTaskStatus from '@/components/VTaskStatus.vue';
import TextSvg from '@/assets/text.svg';
import EmailSvg from '@/assets/email.svg';
import AttachmentSvg from '@/assets/attachment.svg';
import { Text } from 'vue';

function quarterColumn(quarter: number, visible = false): ITableColumn<ITask> {
    return {
        key: 'quarter' + quarter,
        orderKey: 'deadline',
        render: (task: ITask) => {
            const { deadline, previousApprovedDeadline, deadlineChangedComment, status } = task;

            if (!deadline && !previousApprovedDeadline) return;

            const now = DateTime.now();

            const quarterDeadline = deadline ? Math.floor(new Date(deadline).getMonth() / 3) + 1 : null;

            const quarterPrevDeadline = previousApprovedDeadline
                ? Math.floor(new Date(previousApprovedDeadline).getMonth() / 3) + 1
                : null;

            const tooltip = resolveDirective('tooltip');
            let commentText = deadlineChangedComment
                ? vueI18n.global.t('tooltips.tasks.deadline-changed.reason', {
                      comment: deadlineChangedComment,
                  })
                : '';

            const sameQuarterBothDeadlines =
                quarterDeadline === quarterPrevDeadline &&
                quarterDeadline === quarter &&
                deadline &&
                previousApprovedDeadline &&
                new Date(deadline).getTime() !== new Date(previousApprovedDeadline).getTime();

            if (quarterDeadline === quarter) {
                let className = 'v-table__content--text';

                if (deadline && status === Status.InProgress && DateTime.fromISO(deadline, { zone: 'UTC' }) < now) {
                    className += ' v-table__content--red';
                }

                if (status === Status.Finished || status === Status.Completed) {
                    className += ' v-table__content--green';
                }

                if (sameQuarterBothDeadlines) {
                    className += ' v-table__content--hint';

                    commentText = deadlineChangedComment
                        ? vueI18n.global.t('tooltips.tasks.deadline-changed.from-old-reason', {
                              oldDeadline: formatDateOnly(previousApprovedDeadline),
                              comment: deadlineChangedComment,
                          })
                        : vueI18n.global.t('tooltips.tasks.deadline-changed.from-old', {
                              oldDeadline: formatDateOnly(previousApprovedDeadline),
                              comment: deadlineChangedComment,
                          });

                    return withDirectives(h('span', { class: className }, formatDateOnly(deadline)), [
                        [tooltip, commentText],
                    ]);
                }

                return h('span', { class: className }, deadline ? formatDateOnly(deadline) : '');
            }

            if (quarterPrevDeadline === quarter && previousApprovedDeadline) {
                let className = 'v-table__content--text v-table__content--text-blurry v-table__content--strikethrough';

                className += commentText ? ' v-table__content--hint' : '';

                return withDirectives(
                    h(
                        'span',
                        { class: className },
                        previousApprovedDeadline ? formatDateOnly(previousApprovedDeadline) : '',
                    ),
                    [[tooltip, commentText]],
                );
            }
        },
        title: vueI18n.global.t('quarter' + quarter),
        visible: visible,
        required: false,
        sortable: true,
        draggable: true,
        minWidth: 5,
    };
}

export const allColumns = {
    title: {
        key: 'title',
        orderKey: 'title',
        render: (task: ITask) => {
            let titleClassName = 'text text--overflow text--left';

            if (!task.title || task.status === Status.Finished || task.status === Status.Completed) {
                titleClassName = titleClassName + ' text--blurry';
            }

            if (task.status === Status.Finished || task.status === Status.Completed) {
                titleClassName = titleClassName + ' text--line-through text--italic-with-padding';
            }

            const children = [
                h(VTaskStatus, { task, showTitle: false }),
                h('span', { className: titleClassName }, task.title ? task.title : vueI18n.global.t('untitled')),
            ];

            if (task.description) {
                children.push(h(TextSvg, { class: 'svg svg--150' }));
            }

            if (typeof task.commentsCount === 'number' && task.commentsCount > 0) {
                children.push(h(EmailSvg, { class: 'svg svg--150' }));
                children.push(h(Text, task.commentsCount));
            }

            if (typeof task.attachmentsCount === 'number' && task.attachmentsCount > 0) {
                children.push(h(AttachmentSvg, { class: 'svg svg--150' }));
            }

            children.push(h('span', { style: { width: '0.5rem' } }));

            return h(
                'div',
                { className: 'v-table__content v-table__content--center v-table__content--shrink' },
                children,
            );
        },
        title: vueI18n.global.t('task'),
        visible: true,
        required: true,
        draggable: false,
        sortable: true,
        minWidth: 20,
    } as ITableColumn<ITask>,
    description: {
        key: 'description',
        orderKey: 'description',
        render: (task: ITask) => {
            const className = 'v-table__content--text';

            return h('span', { className }, task?.description);
        },
        title: vueI18n.global.t('description'),
        visible: false,
        required: false,
        draggable: true,
        sortable: true,
        minWidth: 5,
    } as ITableColumn<ITask>,
    deadline: {
        key: 'deadline',
        orderKey: 'deadline',
        render: (task: ITask) => {
            const { deadlineChangedComment, previousApprovedDeadline } = task;

            let className = 'v-table__content--text';

            if (
                task.deadline &&
                task.status === Status.InProgress &&
                DateTime.fromISO(task.deadline, { zone: 'UTC' }) < DateTime.now()
            ) {
                className = className + ' v-table__content--red';
            }

            if (task.status === Status.Finished || task.status === Status.Completed) {
                className = className + ' v-table__content--green';
            }

            if (previousApprovedDeadline && task.deadline != previousApprovedDeadline) {
                className = className + ' v-table__content--text-red';

                const tooltip = resolveDirective('tooltip');
                const commentText = deadlineChangedComment
                    ? vueI18n.global.t('tooltips.tasks.deadline-changed.from-old-reason', {
                          oldDeadline: formatDateOnly(previousApprovedDeadline),
                          comment: deadlineChangedComment,
                      })
                    : vueI18n.global.t('tooltips.tasks.deadline-changed.from-old', {
                          oldDeadline: formatDateOnly(previousApprovedDeadline),
                      });

                return withDirectives(h('span', { className }, task?.deadline ? formatDateOnly(task.deadline) : ''), [
                    [tooltip, commentText],
                ]);
            }

            return h('span', { className }, task?.deadline ? formatDateOnly(task.deadline) : '');
        },
        title: vueI18n.global.t('deadline'),
        visible: true,
        required: false,
        sortable: true,
        draggable: true,
        minWidth: 5,
    } as ITableColumn<ITask>,
    author: {
        key: 'author',
        orderKey: 'author',
        render: (task: ITask) =>
            task.author
                ? h(
                      'div',
                      { className: 'v-table__content v-table__content--center v-table__content--margin' },
                      h(VUserCard, { user: task.author, tiny: true }),
                  )
                : null,
        title: vueI18n.global.t('author'),
        visible: true,
        required: false,
        sortable: true,
        draggable: true,
        className: 'v-table__content--center v-table__content--margin',
    } as ITableColumn<ITask>,
    assignee: {
        key: 'assignee',
        orderKey: 'assignee.displayName',
        render: (task: ITask) => {
            const assignee = task.collaborators?.find(
                (collaborator) => collaborator.role === CollaboratorRole.Assignee,
            );

            return assignee?.user
                ? h(
                      'div',
                      { className: 'v-table__content v-table__content--center v-table__content--margin' },
                      h(VUserCard, { user: assignee.user, tiny: true }),
                  )
                : null;
        },
        title: vueI18n.global.t('assignee'),
        visible: true,
        required: false,
        sortable: true,
        draggable: true,
    } as ITableColumn<ITask>,
    status: {
        key: 'status',
        orderKey: 'status',
        render: (task: ITask) => {
            return h('div', { className: 'v-table__content v-table__content--center' }, [h(VTaskStatus, { task })]);
        },
        title: vueI18n.global.t('status'),
        visible: false,
        required: false,
        sortable: true,
        draggable: true,
    } as ITableColumn<ITask>,
    quarter1: quarterColumn(1),
    quarter2: quarterColumn(2),
    quarter3: quarterColumn(3),
    quarter4: quarterColumn(4),
};

export const defaultColumns: ITableColumn<ITask>[] = Object.values(allColumns);
export const journalColumns: ITableColumn<ITask>[] = [
    allColumns.title,
    allColumns.author,
    allColumns.status,
    allColumns.assignee,
    allColumns.description,
    quarterColumn(1, true),
    quarterColumn(2, true),
    quarterColumn(3, true),
    quarterColumn(4, true),
];

export default function useTaskTableViewer(key: string, table: ITable<ITask>) {
    const raw = Storages.Settings.get<Partial<ITable<ITask>>>(key);
    const local = ref(table);

    table.columns = table.columns
        .map((column) => {
            const index = raw?.columns?.findIndex((item) => item.key === column.key) ?? -1;

            if (index === -1) {
                return {
                    index: 0,
                    ...column,
                };
            }

            const carry = raw?.columns?.[index];
            return {
                index,
                ...column,
                width: carry?.width ?? column.width,
                visible: carry?.visible ?? column.visible,
            };
        })
        .sort((left, right) => left.index - right.index);

    const result = computed<ITable>({
        get: () => local.value as ITable,
        set: (value: ITable<ITask>) => {
            local.value = value;
            Storages.Settings.set<Partial<ITable<ITask>>>(key, {
                columns: local.value.columns.map((column) => ({
                    key: column.key,
                    width: column.width,
                    visible: column.visible,
                })),
            } as Partial<ITable<ITask>>);
        },
    });

    return result;
}
